import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
} from "react";
import useLocalStorage from "../hooks/useLocalStorage";

const DataContext = createContext(null);

const STORAGE_KEY = "clearpath:data";

const DAY_MS = 24 * 60 * 60 * 1000;

const INITIAL_TRUSTED = [
    {
        id: "jenny",
        name: "Jenny",
        relation: "Daughter",
        avatarSeed: "jenny",
        isPrimary: false,
    },
    {
        id: "tom",
        name: "Tom",
        relation: "Husband",
        avatarSeed: "tom",
        isPrimary: true,
    },
];

/** Deterministic seed history — 7 days of glucose (4/day) + BP (1/day) */
function generateSeed() {
    const now = Date.now();
    const glucose = [];
    const bp = [];
    for (let d = 6; d >= 0; d--) {
        const dayStart = new Date(now - d * DAY_MS).setHours(0, 0, 0, 0);
        // 4 glucose readings/day at 8am/12pm/5pm/9pm
        [
            { hr: 8, base: 102 },
            { hr: 12, base: 135 },
            { hr: 17, base: 128 },
            { hr: 21, base: 112 },
        ].forEach(({ hr, base }, i) => {
            const ts = dayStart + hr * 60 * 60 * 1000;
            const jitter = ((d * 7 + i * 3) % 9) - 4; // -4..+4
            glucose.push({ value: base + jitter, ts });
        });
        // 1 BP per day at 9am
        const ts = dayStart + 9 * 60 * 60 * 1000;
        const sys = 120 + (((d * 11) % 7) - 3);
        const dia = 76 + (((d * 5) % 5) - 2);
        bp.push({ systolic: sys, diastolic: dia, pulse: 68 + ((d * 3) % 5) - 2, ts });
    }
    return { glucose, bp };
}

function loadDefaults() {
    const seed = generateSeed();
    return {
        trustedCircle: INITIAL_TRUSTED,
        glucoseHistory: seed.glucose,
        bpHistory: seed.bp,
        adherence: 0.92, // 92% medication adherence over the week
        moodLog: ["Pretty good", "Tired today", "Great walk", "OK"],
        sentReplies: [], // { id, messageId, text, ts }
        paidBills: [], // array of bill ids (strings)
    };
}

export function DataProvider({ children }) {
    const [state, setState, reset] = useLocalStorage(STORAGE_KEY, loadDefaults());

    const addContact = useCallback(
        (contact) => {
            setState((s) => ({
                ...s,
                trustedCircle: [
                    ...s.trustedCircle,
                    {
                        id: `c_${Date.now()}`,
                        avatarSeed: contact.avatarSeed || "default",
                        isPrimary: false,
                        ...contact,
                    },
                ],
            }));
        },
        [setState],
    );

    const addGlucose = useCallback(
        (value) => {
            setState((s) => ({
                ...s,
                glucoseHistory: [
                    ...s.glucoseHistory,
                    { value, ts: Date.now() },
                ],
            }));
        },
        [setState],
    );

    const addBp = useCallback(
        (reading) => {
            setState((s) => ({
                ...s,
                bpHistory: [
                    ...s.bpHistory,
                    { ...reading, ts: Date.now() },
                ],
            }));
        },
        [setState],
    );

    const addReply = useCallback(
        (messageId, text) => {
            setState((s) => ({
                ...s,
                sentReplies: [
                    ...(s.sentReplies || []),
                    {
                        id: `r_${Date.now()}`,
                        messageId,
                        text,
                        ts: Date.now(),
                    },
                ],
            }));
        },
        [setState],
    );

    const markBillPaid = useCallback(
        (billId) => {
            setState((s) => {
                const existing = s.paidBills || [];
                if (existing.includes(billId)) return s;
                return { ...s, paidBills: [...existing, billId] };
            });
        },
        [setState],
    );

    const repliesByMessage = useMemo(() => {
        const map = {};
        (state.sentReplies || []).forEach((r) => {
            if (!map[r.messageId]) map[r.messageId] = [];
            map[r.messageId].push(r);
        });
        return map;
    }, [state.sentReplies]);

    const resetDemo = useCallback(() => {
        reset();
        // ensure fresh seed
        setState(loadDefaults());
    }, [reset, setState]);

    const latestGlucose = useMemo(() => {
        const h = state.glucoseHistory || [];
        return h[h.length - 1] || { value: 118, ts: Date.now() };
    }, [state.glucoseHistory]);

    const latestBp = useMemo(() => {
        const h = state.bpHistory || [];
        return h[h.length - 1] || { systolic: 122, diastolic: 78, pulse: 68, ts: Date.now() };
    }, [state.bpHistory]);

    const primaryContact = useMemo(
        () =>
            state.trustedCircle.find((c) => c.isPrimary) ||
            state.trustedCircle[0],
        [state.trustedCircle],
    );
    const alertContact = useMemo(
        () =>
            state.trustedCircle.find(
                (c) => !c.isPrimary && c.id !== primaryContact?.id,
            ) || state.trustedCircle[1] || primaryContact,
        [state.trustedCircle, primaryContact],
    );

    const value = useMemo(
        () => ({
            trustedCircle: state.trustedCircle,
            glucoseHistory: state.glucoseHistory,
            bpHistory: state.bpHistory,
            adherence: state.adherence,
            moodLog: state.moodLog,
            sentReplies: state.sentReplies || [],
            repliesByMessage,
            paidBills: state.paidBills || [],
            latestGlucose,
            latestBp,
            primaryContact,
            alertContact,
            addContact,
            addGlucose,
            addBp,
            addReply,
            markBillPaid,
            resetDemo,
        }),
        [
            state,
            repliesByMessage,
            latestGlucose,
            latestBp,
            primaryContact,
            alertContact,
            addContact,
            addGlucose,
            addBp,
            addReply,
            markBillPaid,
            resetDemo,
        ],
    );

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error("useData must be used within DataProvider");
    return ctx;
}
